import { createFrontendPlugin } from '@backstage/frontend-plugin-api';
import { PageBlueprint } from '@backstage/frontend-plugin-api';

/**
 * MAC Platform custom pages plugin.
 *
 * Registers all custom section pages (Platform, Agentic Solutions, Resources,
 * Stories, Docs, Roadmap, Community) as routable Backstage pages with
 * IMDB-styled layouts.
 */

// --- Platform pages ---

const contextLakePage = PageBlueprint.make({
  name: 'context-lake',
  params: {
    path: '/platform/context-lake',
    title: 'Context Lake',
    loader: () =>
      import('./platform/ContextLakePage').then(m => <m.ContextLakePage />),
  },
});

const actionsPage = PageBlueprint.make({
  name: 'actions',
  params: {
    path: '/platform/actions',
    title: 'Actions',
    loader: () =>
      import('./platform/ActionsPage').then(m => <m.ActionsPage />),
  },
});

const aiAgentsPage = PageBlueprint.make({
  name: 'ai-agents',
  params: {
    path: '/platform/ai-agents',
    title: 'AI Agents',
    loader: () =>
      import('./platform/AIAgentsPage').then(m => <m.AIAgentsPage />),
  },
});

const scorecardsPage = PageBlueprint.make({
  name: 'scorecards',
  params: {
    path: '/platform/scorecards',
    title: 'Scorecards',
    loader: () =>
      import('./platform/ScorecardsPage').then(m => <m.ScorecardsPage />),
  },
});

const workflowOrchestratorPage = PageBlueprint.make({
  name: 'workflow-orchestrator',
  params: {
    path: '/platform/workflow-orchestrator',
    title: 'Workflow Orchestrator',
    loader: () =>
      import('./platform/WorkflowOrchestratorPage').then(
        m => <m.WorkflowOrchestratorPage />,
      ),
  },
});

const interfaceDesignerPage = PageBlueprint.make({
  name: 'interface-designer',
  params: {
    path: '/platform/interface-designer',
    title: 'Interface Designer',
    loader: () =>
      import('./platform/InterfaceDesignerPage').then(
        m => <m.InterfaceDesignerPage />,
      ),
  },
});

const accessControlsPage = PageBlueprint.make({
  name: 'access-controls',
  params: {
    path: '/platform/access-controls',
    title: 'Access Controls',
    loader: () =>
      import('./platform/AccessControlsPage').then(
        m => <m.AccessControlsPage />,
      ),
  },
});

const integrationsPage = PageBlueprint.make({
  name: 'integrations',
  params: {
    path: '/platform/integrations',
    title: 'Integrations',
    loader: () =>
      import('./platform/IntegrationsPage').then(m => <m.IntegrationsPage />),
  },
});

const mcpRegistryPage = PageBlueprint.make({
  name: 'mcp-registry',
  params: {
    path: '/platform/mcp-registry',
    title: 'MCP Registry',
    loader: () =>
      import('./platform/McpRegistryPage').then(m => <m.McpRegistryPage />),
  },
});

// --- Agentic Solutions pages ---

const ticketResolutionPage = PageBlueprint.make({
  name: 'ticket-resolution',
  params: {
    path: '/agentic/ticket-resolution',
    title: 'Autonomous Ticket Resolution',
    loader: () =>
      import('./agentic/TicketResolutionPage').then(
        m => <m.TicketResolutionPage />,
      ),
  },
});

const selfHealingPage = PageBlueprint.make({
  name: 'self-healing',
  params: {
    path: '/agentic/self-healing',
    title: 'Self-Healing Incidents',
    loader: () =>
      import('./agentic/SelfHealingPage').then(m => <m.SelfHealingPage />),
  },
});

const engineeringIntelligencePage = PageBlueprint.make({
  name: 'engineering-intelligence',
  params: {
    path: '/agentic/engineering-intelligence',
    title: 'Engineering Intelligence',
    loader: () =>
      import('./agentic/EngineeringIntelligencePage').then(
        m => <m.EngineeringIntelligencePage />,
      ),
  },
});

const resourceManagementPage = PageBlueprint.make({
  name: 'resource-management',
  params: {
    path: '/agentic/resource-management',
    title: 'Resource Management',
    loader: () =>
      import('./agentic/ResourceManagementPage').then(
        m => <m.ResourceManagementPage />,
      ),
  },
});

const agenticWorkManagementPage = PageBlueprint.make({
  name: 'agentic-work-management',
  params: {
    path: '/agentic/work-management',
    title: 'Agentic Work Management',
    loader: () =>
      import('./agentic/AgenticWorkManagementPage').then(
        m => <m.AgenticWorkManagementPage />,
      ),
  },
});

// --- Resources pages ---

const blogPage = PageBlueprint.make({
  name: 'blog',
  params: {
    path: '/resources/blog',
    title: 'Blog',
    loader: () =>
      import('./resources/BlogPage').then(m => <m.BlogPage />),
  },
});

const eventsPage = PageBlueprint.make({
  name: 'events',
  params: {
    path: '/resources/events',
    title: 'Events & Webinars',
    loader: () =>
      import('./resources/EventsPage').then(m => <m.EventsPage />),
  },
});

const resourceCenterPage = PageBlueprint.make({
  name: 'resource-center',
  params: {
    path: '/resources/center',
    title: 'Resource Center',
    loader: () =>
      import('./resources/ResourceCenterPage').then(
        m => <m.ResourceCenterPage />,
      ),
  },
});

// --- Port.io-inspired pages ---

const orgHomePage = PageBlueprint.make({
  name: 'org-home',
  params: {
    path: '/home',
    title: 'Organization Home',
    loader: () =>
      import('./portio/OrgHomePage').then(m => <m.OrgHomePage />),
  },
});

const serviceCatalogPage = PageBlueprint.make({
  name: 'service-catalog',
  params: {
    path: '/service-catalog',
    title: 'Service Catalog',
    loader: () =>
      import('./portio/ServiceCatalogPage').then(m => <m.ServiceCatalogPage />),
  },
});

const selfServePage = PageBlueprint.make({
  name: 'self-serve',
  params: {
    path: '/self-serve',
    title: 'Self-Serve',
    loader: () =>
      import('./portio/SelfServePage').then(m => <m.SelfServePage />),
  },
});

const dataModelPage = PageBlueprint.make({
  name: 'data-model',
  params: {
    path: '/data-model',
    title: 'Data Model',
    loader: () =>
      import('./portio/DataModelPage').then(m => <m.DataModelPage />),
  },
});

const aiInsightsPage = PageBlueprint.make({
  name: 'ai-insights',
  params: {
    path: '/ai-insights',
    title: 'AI Insights',
    loader: () =>
      import('./portio/AIInsightsPage').then(m => <m.AIInsightsPage />),
  },
});

const myWorkItemsPage = PageBlueprint.make({
  name: 'my-work-items',
  params: {
    path: '/my-work',
    title: 'My Work Items',
    loader: () =>
      import('./portio/MyWorkItemsPage').then(m => <m.MyWorkItemsPage />),
  },
});

const aiDashboardsPage = PageBlueprint.make({
  name: 'ai-dashboards',
  params: {
    path: '/ai-dashboards',
    title: 'AI Dashboards & Quick Actions',
    loader: () =>
      import('./portio/AIDashboardsPage').then(m => <m.AIDashboardsPage />),
  },
});

const incidentsDashboardPage = PageBlueprint.make({
  name: 'incidents-dashboard',
  params: {
    path: '/incidents',
    title: 'Incidents Dashboard',
    loader: () =>
      import('./portio/IncidentsDashboardPage').then(
        m => <m.IncidentsDashboardPage />,
      ),
  },
});

const cloudResourcesPage = PageBlueprint.make({
  name: 'cloud-resources',
  params: {
    path: '/cloud-resources',
    title: 'Cloud Resources',
    loader: () =>
      import('./portio/CloudResourcesPage').then(m => <m.CloudResourcesPage />),
  },
});

// --- Top-level pages ---

const storiesPage = PageBlueprint.make({
  name: 'stories',
  params: {
    path: '/stories',
    title: 'Stories',
    loader: () =>
      import('./StoriesPage').then(m => <m.StoriesPage />),
  },
});

const roadmapPage = PageBlueprint.make({
  name: 'roadmap',
  params: {
    path: '/roadmap',
    title: 'Public Roadmap',
    loader: () =>
      import('./RoadmapPage').then(m => <m.RoadmapPage />),
  },
});

const communityPage = PageBlueprint.make({
  name: 'community',
  params: {
    path: '/community',
    title: 'Community',
    loader: () =>
      import('./CommunityPage').then(m => <m.CommunityPage />),
  },
});

export const macPagesPlugin = createFrontendPlugin({
  pluginId: 'mac-pages',
  extensions: [
    // Platform
    contextLakePage,
    actionsPage,
    aiAgentsPage,
    scorecardsPage,
    workflowOrchestratorPage,
    interfaceDesignerPage,
    accessControlsPage,
    integrationsPage,
    mcpRegistryPage,
    // Agentic Solutions
    ticketResolutionPage,
    selfHealingPage,
    engineeringIntelligencePage,
    resourceManagementPage,
    agenticWorkManagementPage,
    // Resources
    blogPage,
    eventsPage,
    resourceCenterPage,
    // Port.io Dashboards
    orgHomePage,
    serviceCatalogPage,
    selfServePage,
    dataModelPage,
    aiInsightsPage,
    myWorkItemsPage,
    aiDashboardsPage,
    incidentsDashboardPage,
    cloudResourcesPage,
    // Top-level
    storiesPage,
    roadmapPage,
    communityPage,
  ],
});
